import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import { googleLogin, appleLogin, getConsent } from "./api";
import AppDownloadNudge from "./components/AppDownloadNudge";
import RenderTranslatedText from "../../components/RenderTranslatedText";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;

export default function ShopCustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const redirectTarget = location.state?.from || "/shop/shops";

  const proceedAfterAuth = async (user) => {
    const needsBasicDetails = !user?.dob || !user?.nickname;
    if (needsBasicDetails) {
      navigate("/shop/onboarding", { state: { from: redirectTarget, user } });
      return;
    }
    try {
      const consentRes = await getConsent();
      const consent = consentRes.data || {};
      if (!consent.eulaAccepted || !consent.dataProcessingAccepted) {
        navigate("/shop/onboarding", {
          state: { from: redirectTarget, user },
        });
        return;
      }
    } catch {
      navigate("/shop/onboarding", { state: { from: redirectTarget, user } });
      return;
    }
    navigate(redirectTarget, { replace: true });
  };

  const handleAuthResult = async (result) => {
    if (!result || result.error || !result.token) {
      setError("Login failed. Please try again.");
      setLoading(false);
      return;
    }
    localStorage.setItem("JWTtoken", result.token);
    localStorage.setItem("userId", result.user?.cuid || result.user?.id || "");
    localStorage.setItem("shopUserNickname", result.user?.nickname || "");
    localStorage.setItem("shopUserEmail", result.user?.email || "");
    await proceedAfterAuth(result.user);
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    setLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential);
      await handleAuthResult(result);
    } catch (err) {
      setError("Could not sign in with Google. Please try again.");
      setLoading(false);
    }
  };

  const handleAppleCallback = async (res) => {
    if (!res?.authorization?.id_token) {
      setError("Apple sign-in was cancelled or failed.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await appleLogin(res.authorization.id_token);
      await handleAuthResult(result);
    } catch (err) {
      setError("Could not sign in with Apple. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 pt-16 pb-8">
      <div className="flex-1 flex flex-col items-center text-center gap-3">
        <p className="otaFont font-extrabold text-[42px] leading-none">Ōta</p>
        <h1 className="text-xl font-semibold otaDark mt-4">
          <RenderTranslatedText i8nKey="shop_login_title" />
        </h1>
        <p className="otaLight text-sm max-w-[280px]">
          <RenderTranslatedText i8nKey="shop_login_subtitle" />
        </p>
      </div>

      <div className="flex flex-col gap-4 my-8">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
            {error}
          </div>
        )}

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError("Google sign-in failed.")}
            useOneTap={false}
            width="300"
          />
        </div>

        <AppleLogin
          clientId={APPLE_CLIENT_ID}
          redirectURI={`${window.location.origin}/shop/login`}
          scope="email name"
          responseMode="form_post"
          usePopup={true}
          callback={handleAppleCallback}
          render={(props) => (
            <button
              onClick={props.onClick}
              disabled={loading}
              className="w-full h-11 rounded-lg border border-gray-300 flex items-center justify-center gap-2 font-semibold text-sm otaDark disabled:opacity-60"
            >
              <RenderTranslatedText i8nKey="shop_login_apple" />
            </button>
          )}
        />

        {loading && (
          <p className="text-center text-sm otaLight">Signing you in…</p>
        )}
      </div>

      <AppDownloadNudge />
    </div>
  );
}
